import Authentication from '@/common/Authentication'
import Session from '@/common/Session'

/**
 * Compares the build-time APP_VERSION with the deployed /version.txt
 * so long-lived tabs can detect a new release and prompt the user to reload.
 *
 * version.txt format (emitted by the Vite version-txt plugin):
 *   x.y.z
 */
export default class VersionService {
  static versionFileName = 'version.txt'
  /** Once true, the update popup will not be opened again until a full page load. */
  static hasPromptedUpdate = false
  static listeners = new Set()

  static staleAssetErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed'
  ]

  static getCurrentVersion () {
    return APP_VERSION
  }

  static getVersionUrl () {
    const now = Date.now()
    return `/${this.versionFileName}?timestamp=${now}`
  }

  static parseVersion (source) {
    const version = String(source || '').trim()
    return version || null
  }

  static subscribe (listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  static notifyUpdateAvailable (detail) {
    this.listeners.forEach((listener) => listener(detail))
  }

  /**
   * Detects Vite dynamic-import failures that typically mean the deployed
   * assets no longer match this client.
   */
  static isStaleAssetError (error) {
    if (!error) {
      return false
    }
    const message = error.message || String(error)
    return this.staleAssetErrorPatterns.some((pattern) => message.includes(pattern))
  }

  /**
   * Opens the version-update popup at most once per page load.
   * @param {string} [detail] optional context for listeners (e.g. remote version or reason)
   */
  static promptUpdate (detail = 'stale-assets') {
    if (this.hasPromptedUpdate) {
      return
    }
    this.hasPromptedUpdate = true
    this.notifyUpdateAvailable(detail)
  }

  /**
   * Ends the local session, logs the user out, and navigates away
   * (Authentication.logout performs a full page load via redirect).
   */
  static async reload () {
    Session.stop()
    await Authentication.logout()
  }

  static async checkVersion () {
    try {
      const response = await fetch(this.getVersionUrl())
      if (!response.ok) {
        return
      }
      const text = await response.text()
      const remoteVersion = this.parseVersion(text)
      if (!remoteVersion || remoteVersion === this.getCurrentVersion()) {
        return
      }
      this.promptUpdate(remoteVersion)
    } catch {
      // Do not block navigation on fetch/parse failures
    }
  }
}
